import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Gavel, ArrowLeft, Loader2, DollarSign, Target, AlertTriangle, MessageSquare, Clock, Shield, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useTheme } from '../context/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NegotiationCoachPage = (props) => {
  const params = useParams();
  const sessionId = props.sessionId ?? params.sessionId;
  const backTo = props.backTo || (sessionId ? `/vip/${sessionId}/dashboard` : '/');
  const backLabel = props.backLabel || (sessionId ? 'VIP Dashboard' : 'Home');
  const { isDark } = useTheme();
  const [form, setForm] = useState({
    rv_year: '', rv_brand: '', rv_model: '', listing_price: '',
    condition: 'used', mileage: '', dealer_or_private: 'dealer', additional_info: ''
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedSection, setExpandedSection] = useState('script');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.rv_year || !form.rv_brand || !form.rv_model || !form.listing_price) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        rv_year: parseInt(form.rv_year),
        rv_brand: form.rv_brand,
        rv_model: form.rv_model,
        listing_price: parseFloat(form.listing_price),
        condition: form.condition,
        mileage: form.mileage ? parseInt(form.mileage) : null,
        dealer_or_private: form.dealer_or_private,
        additional_info: form.additional_info || null
      };
      const res = await axios.post(`${API}/tools/negotiation-coach`, payload);
      setResult(res.data.analysis);
    } catch {
      toast.error('AI analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const bg = isDark ? '#0a0a0f' : '#f8f9fa';
  const cardBg = isDark ? '#141420' : '#fff';
  const border = isDark ? '#1e1e30' : '#e5e7eb';
  const textPrimary = isDark ? '#f0f0f5' : '#111827';
  const textSecondary = isDark ? '#8b8ba3' : '#6b7280';
  const accent = '#6366f1';

  const assessmentColors = {
    'overpriced': '#ef4444', 'fair': '#f59e0b', 'good_deal': '#22c55e', 'steal': '#10b981'
  };

  const toggleSection = (s) => setExpandedSection(expandedSection === s ? null : s);

  return (
    <div data-testid="negotiation-coach-page" style={{ background: bg, minHeight: '100vh', fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* Header */}
      <div style={{ background: isDark ? '#101018' : '#fff', borderBottom: `1px solid ${border}`, padding: '16px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to={backTo} style={{ color: textSecondary, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
            <ArrowLeft size={18} /> {backLabel}
          </Link>
          <span style={{ color: textSecondary }}>/</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Gavel size={18} style={{ color: accent }} />
            <span style={{ color: accent, fontWeight: 700, fontSize: 15 }}>Negotiation Coach</span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px' }}>
        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: `${accent}15`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: accent, marginBottom: 12 }}>
            <Gavel size={28} />
          </div>
          <h1 style={{ color: textPrimary, fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em' }}>AI Negotiation Coach</h1>
          <p style={{ color: textSecondary, fontSize: 15, maxWidth: 500, margin: '8px auto 0' }}>
            Enter the RV details below and get a complete negotiation playbook with exact dollar amounts.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: result ? '380px 1fr' : '1fr', gap: 24, alignItems: 'start' }}>
          {/* Form */}
          <form onSubmit={handleSubmit} data-testid="negotiation-form" style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Year *</Label>
                <Input data-testid="nego-year" type="number" value={form.rv_year} onChange={e => setForm(f => ({...f, rv_year: e.target.value}))} placeholder="2022" style={{ background: isDark ? '#1a1a28' : '#f9fafb', borderColor: border, color: textPrimary }} />
              </div>
              <div>
                <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Brand *</Label>
                <Input data-testid="nego-brand" value={form.rv_brand} onChange={e => setForm(f => ({...f, rv_brand: e.target.value}))} placeholder="Thor Motor Coach" style={{ background: isDark ? '#1a1a28' : '#f9fafb', borderColor: border, color: textPrimary }} />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Model *</Label>
                <Input data-testid="nego-model" value={form.rv_model} onChange={e => setForm(f => ({...f, rv_model: e.target.value}))} placeholder="Palazzo 33.5" style={{ background: isDark ? '#1a1a28' : '#f9fafb', borderColor: border, color: textPrimary }} />
              </div>
              <div>
                <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Listing Price *</Label>
                <Input data-testid="nego-price" type="number" value={form.listing_price} onChange={e => setForm(f => ({...f, listing_price: e.target.value}))} placeholder="85000" style={{ background: isDark ? '#1a1a28' : '#f9fafb', borderColor: border, color: textPrimary }} />
              </div>
              <div>
                <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Mileage</Label>
                <Input data-testid="nego-mileage" type="number" value={form.mileage} onChange={e => setForm(f => ({...f, mileage: e.target.value}))} placeholder="25000" style={{ background: isDark ? '#1a1a28' : '#f9fafb', borderColor: border, color: textPrimary }} />
              </div>
              <div>
                <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Condition</Label>
                <select data-testid="nego-condition" value={form.condition} onChange={e => setForm(f => ({...f, condition: e.target.value}))}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${border}`, background: isDark ? '#1a1a28' : '#f9fafb', color: textPrimary, fontSize: 14 }}>
                  <option value="new">New</option>
                  <option value="like_new">Like New</option>
                  <option value="used">Used</option>
                  <option value="fair">Fair</option>
                </select>
              </div>
              <div>
                <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Seller Type</Label>
                <select data-testid="nego-seller" value={form.dealer_or_private} onChange={e => setForm(f => ({...f, dealer_or_private: e.target.value}))}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${border}`, background: isDark ? '#1a1a28' : '#f9fafb', color: textPrimary, fontSize: 14 }}>
                  <option value="dealer">Dealership</option>
                  <option value="private">Private Seller</option>
                </select>
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Additional Info</Label>
              <textarea data-testid="nego-info" value={form.additional_info} onChange={e => setForm(f => ({...f, additional_info: e.target.value}))}
                placeholder="Any details about the listing, damage, upgrades, etc." rows={3}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${border}`, background: isDark ? '#1a1a28' : '#f9fafb', color: textPrimary, fontSize: 14, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>
            <Button data-testid="nego-submit" type="submit" disabled={loading}
              style={{ width: '100%', marginTop: 16, background: accent, color: '#fff', fontWeight: 600, border: 'none', height: 44 }}>
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" style={{ marginRight: 8 }} /> Analyzing...</> : 'Get Negotiation Strategy'}
            </Button>
          </form>

          {/* Results */}
          {result && !result.raw_response && (
            <div data-testid="negotiation-results" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Market Analysis Card */}
              {result.market_analysis && (
                <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
                  <h3 style={{ color: textPrimary, fontSize: 16, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Target size={18} style={{ color: accent }} /> Market Analysis
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
                    <PriceCard label="Fair Market Value" value={result.market_analysis.estimated_fair_value} isDark={isDark} border={border} />
                    <PriceCard label="Price Range Low" value={result.market_analysis.price_range_low} isDark={isDark} border={border} />
                    <PriceCard label="Price Range High" value={result.market_analysis.price_range_high} isDark={isDark} border={border} />
                  </div>
                  <div style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 6, background: `${assessmentColors[result.market_analysis.listing_assessment] || '#6b7280'}20`, color: assessmentColors[result.market_analysis.listing_assessment] || '#6b7280', fontSize: 13, fontWeight: 600, textTransform: 'uppercase' }}>
                    {(result.market_analysis.listing_assessment || '').replace('_', ' ')}
                  </div>
                </div>
              )}

              {/* Offer Strategy */}
              <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${accent}30`, padding: 24 }}>
                <h3 style={{ color: textPrimary, fontSize: 16, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <DollarSign size={18} style={{ color: '#22c55e' }} /> Your Offer Strategy
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                  {result.opening_offer && (
                    <div style={{ padding: 14, borderRadius: 10, background: isDark ? '#1a2e1a' : '#f0fdf4', border: '1px solid #22c55e30' }}>
                      <p style={{ color: '#22c55e', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em' }}>OPENING OFFER</p>
                      <p style={{ color: textPrimary, fontSize: 20, fontWeight: 800, marginTop: 4 }}>${(result.opening_offer.amount || 0).toLocaleString()}</p>
                      <p style={{ color: textSecondary, fontSize: 12, marginTop: 2 }}>{result.opening_offer.percent_below_asking}% below asking</p>
                    </div>
                  )}
                  {result.target_price && (
                    <div style={{ padding: 14, borderRadius: 10, background: isDark ? '#1a1a2e' : '#eff6ff', border: `1px solid ${accent}30` }}>
                      <p style={{ color: accent, fontSize: 11, fontWeight: 600, letterSpacing: '0.05em' }}>TARGET PRICE</p>
                      <p style={{ color: textPrimary, fontSize: 20, fontWeight: 800, marginTop: 4 }}>${(result.target_price.amount || 0).toLocaleString()}</p>
                      <p style={{ color: textSecondary, fontSize: 12, marginTop: 2 }}>{result.target_price.percent_below_asking}% below asking</p>
                    </div>
                  )}
                  {result.walk_away_price && (
                    <div style={{ padding: 14, borderRadius: 10, background: isDark ? '#2e1a1a' : '#fef2f2', border: '1px solid #ef444430' }}>
                      <p style={{ color: '#ef4444', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em' }}>WALK AWAY</p>
                      <p style={{ color: textPrimary, fontSize: 20, fontWeight: 800, marginTop: 4 }}>${(result.walk_away_price.amount || 0).toLocaleString()}</p>
                      <p style={{ color: textSecondary, fontSize: 12, marginTop: 2 }}>Don't go above this</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Expandable Sections */}
              <CollapsibleSection title="Negotiation Script" icon={<MessageSquare size={16} />} expanded={expandedSection === 'script'} onToggle={() => toggleSection('script')} isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary}>
                <p style={{ color: textSecondary, fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{result.negotiation_script}</p>
              </CollapsibleSection>

              <CollapsibleSection title="Talking Points" icon={<Shield size={16} />} expanded={expandedSection === 'points'} onToggle={() => toggleSection('points')} isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary}>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {(result.talking_points || []).map((p, i) => (
                    <li key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: `1px solid ${border}` }}>
                      <span style={{ color: accent, fontWeight: 700, minWidth: 20 }}>{i + 1}.</span>
                      <span style={{ color: textSecondary, fontSize: 14 }}>{p}</span>
                    </li>
                  ))}
                </ul>
              </CollapsibleSection>

              <CollapsibleSection title="Red Flags to Watch" icon={<AlertTriangle size={16} />} expanded={expandedSection === 'flags'} onToggle={() => toggleSection('flags')} isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary}>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {(result.red_flags_to_watch || []).map((f, i) => (
                    <li key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: `1px solid ${border}` }}>
                      <AlertTriangle size={14} style={{ color: '#ef4444', flexShrink: 0, marginTop: 2 }} />
                      <span style={{ color: textSecondary, fontSize: 14 }}>{f}</span>
                    </li>
                  ))}
                </ul>
              </CollapsibleSection>

              {result.timing_advice && (
                <CollapsibleSection title="Best Time to Buy" icon={<Clock size={16} />} expanded={expandedSection === 'timing'} onToggle={() => toggleSection('timing')} isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary}>
                  <p style={{ color: textSecondary, fontSize: 14, lineHeight: 1.6 }}>{result.timing_advice}</p>
                </CollapsibleSection>
              )}
            </div>
          )}

          {result && result.raw_response && (
            <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
              <p style={{ color: textSecondary, fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{result.raw_response}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PriceCard = ({ label, value, isDark, border }) => (
  <div style={{ padding: 12, borderRadius: 8, background: isDark ? '#1a1a28' : '#f9fafb', border: `1px solid ${border}` }}>
    <p style={{ color: '#8b8ba3', fontSize: 11, fontWeight: 500, letterSpacing: '0.04em' }}>{label}</p>
    <p style={{ color: isDark ? '#f0f0f5' : '#111827', fontSize: 18, fontWeight: 700, marginTop: 2 }}>${(value || 0).toLocaleString()}</p>
  </div>
);

const CollapsibleSection = ({ title, icon, expanded, onToggle, children, isDark, cardBg, border, textPrimary }) => (
  <div style={{ background: cardBg, borderRadius: 12, border: `1px solid ${border}`, overflow: 'hidden' }}>
    <button onClick={onToggle} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', background: 'none', border: 'none', cursor: 'pointer' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: textPrimary, fontSize: 14, fontWeight: 600 }}>
        {icon} {title}
      </span>
      {expanded ? <ChevronDown size={16} style={{ color: '#8b8ba3' }} /> : <ChevronRight size={16} style={{ color: '#8b8ba3' }} />}
    </button>
    {expanded && <div style={{ padding: '0 20px 20px' }}>{children}</div>}
  </div>
);

export default NegotiationCoachPage;
